/**
 * Date-range presets for the analytics dashboard, and the filter that applies one.
 *
 * Every function takes `today` as a `"YYYY-MM-DD"` string rather than reading the clock, so the
 * behaviour is testable and the caller decides what "now" means. Boundaries are computed with UTC
 * arithmetic so a local timezone can never shift one by a day.
 */

import type { EnquiryFact, StatusTransition } from './types'

export type RangePresetId = 'month' | 'last30' | 'quarter' | 'ytd' | 'all'

export interface DateRange {
  from?: string
  to?: string
}

export const RANGE_PRESETS: ReadonlyArray<{ id: RangePresetId; label: string }> = [
  { id: 'month', label: 'This month' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'quarter', label: 'This quarter' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'all', label: 'All time' },
]

function toDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}

function fmt(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function addDays(iso: string, days: number): string {
  const d = toDate(iso)
  d.setUTCDate(d.getUTCDate() + days)
  return fmt(d)
}

export function presetRange(id: RangePresetId, today: string): DateRange {
  if (id === 'all') return {}
  const d = toDate(today)
  const year = d.getUTCFullYear()
  const month = d.getUTCMonth()

  if (id === 'month') return { from: fmt(new Date(Date.UTC(year, month, 1))), to: today }
  if (id === 'last30') return { from: addDays(today, -29), to: today }
  if (id === 'quarter') {
    const quarterStart = Math.floor(month / 3) * 3
    return { from: fmt(new Date(Date.UTC(year, quarterStart, 1))), to: today }
  }
  return { from: fmt(new Date(Date.UTC(year, 0, 1))), to: today }
}

/**
 * The equal-length window immediately before `range`, for the KPI deltas.
 *
 * Null when either bound is missing: an unbounded range has no "previous period", and inventing
 * one would produce a delta that looks measured but is not.
 */
export function previousRange(range: DateRange): DateRange | null {
  if (!range.from || !range.to) return null
  const spanDays =
    Math.round((toDate(range.to).getTime() - toDate(range.from).getTime()) / 86_400_000) + 1
  return { from: addDays(range.from, -spanDays), to: addDays(range.from, -1) }
}

/**
 * Whether each preset is worth offering, given the data actually present.
 *
 * A preset that selects every enquiry is indistinguishable from "All time", and one that selects
 * none has nothing to show. Offering either is a button that appears to do nothing when pressed —
 * which is exactly how a young dataset makes a working control look broken (every enquiry on the
 * bench today was raised inside a single week, so four of the five presets select the same rows).
 *
 * So they are disabled, with the reason attached rather than left for the user to infer. This is
 * about the DATA, not about correctness: the filtering itself works for every preset, and as soon
 * as there is history spanning more than a week these light up on their own.
 *
 * `All time` is never disabled — it is the fallback every other option collapses into.
 */
export interface PresetAvailability {
  id: RangePresetId
  label: string
  /** How many enquiries this preset selects. */
  count: number
  disabled: boolean
  /** Why it is disabled, for the tooltip. Empty when enabled. */
  reason: string
}

export function presetAvailability(
  facts: readonly EnquiryFact[],
  today: string,
): PresetAvailability[] {
  const total = facts.length
  return RANGE_PRESETS.map(({ id, label }) => {
    const count = filterFactsByRange(facts, presetRange(id, today)).length
    if (id === 'all') return { id, label, count, disabled: false, reason: '' }
    if (total === 0) {
      return { id, label, count, disabled: true, reason: 'No enquiries to filter yet.' }
    }
    if (count === 0) {
      return { id, label, count, disabled: true, reason: 'No enquiries were raised in this window.' }
    }
    if (count === total) {
      return {
        id,
        label,
        count,
        disabled: true,
        reason: `Selects all ${total} enquiries — identical to All time, because every enquiry so far falls inside this window.`,
      }
    }
    return { id, label, count, disabled: false, reason: '' }
  })
}

/** Enquiries created within the range. Both bounds inclusive; an empty range keeps everything. */
export function filterFactsByRange(
  facts: readonly EnquiryFact[],
  range: DateRange,
): EnquiryFact[] {
  if (!range.from && !range.to) return [...facts]
  return facts.filter((f) => {
    if (!f.created_on) return false
    if (range.from && f.created_on < range.from) return false
    if (range.to && f.created_on > range.to) return false
    return true
  })
}

/**
 * Transitions belonging to a given set of enquiries.
 *
 * The funnel is scoped by which ENQUIRIES fall in the range, never by when the transition
 * happened: an enquiry raised in June and won in August belongs, whole, to whichever window
 * contains its creation. Filtering transitions by their own date would sever a funnel in half and
 * report drop-off that never happened.
 */
export function transitionsForFacts(
  transitions: readonly StatusTransition[],
  facts: readonly EnquiryFact[],
): StatusTransition[] {
  const ids = new Set(facts.map((f) => f.id))
  return transitions.filter((t) => ids.has(t.enquiry))
}
